import axios from "axios";
import api from "./axiosConfig";
import { AuthResponse, LoginRequest, OperatorRegisterRequest } from "../types";

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string | null;
  errors?: string[] | null;
}

function unwrapAuth<T>(payload: ApiEnvelope<T>): T {
  if (!payload.success || payload.data === undefined) {
    const msg = payload.message || "Request failed";
    const err = new Error(msg) as Error & { apiErrors?: string[] };
    err.apiErrors = payload.errors ?? undefined;
    throw err;
  }
  return payload.data;
}

function unwrapMessage(payload: ApiEnvelope<unknown>): string {
  if (!payload.success) {
    const err = new Error(payload.message || "Request failed") as Error & { apiErrors?: string[] };
    err.apiErrors = payload.errors ?? undefined;
    throw err;
  }
  return payload.message ?? "";
}

function rethrowWithApiMessage(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ApiEnvelope<unknown> | undefined;
    if (body?.message) {
      const err = new Error(body.message) as Error & { apiErrors?: string[] };
      err.apiErrors = body.errors ?? undefined;
      throw err;
    }
  }
  throw error instanceof Error ? error : new Error("Request failed");
}

export const authApi = {
  login: async (payload: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<ApiEnvelope<AuthResponse>>("/auth/login", payload);
    return unwrapAuth(response.data);
  },

  register: async (payload: OperatorRegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<ApiEnvelope<AuthResponse>>("/auth/register", payload);
    return unwrapAuth(response.data);
  },

  forgotPassword: async (email: string): Promise<string> => {
    try {
      const response = await api.post<ApiEnvelope<unknown>>("/auth/forgot-password", { email });
      return unwrapMessage(response.data);
    } catch (e) {
      rethrowWithApiMessage(e);
    }
  },

  resetPassword: async (payload: {
    email: string;
    token: string;
    newPassword: string;
  }): Promise<string> => {
    try {
      const response = await api.post<ApiEnvelope<unknown>>("/auth/reset-password", payload);
      return unwrapMessage(response.data);
    } catch (e) {
      rethrowWithApiMessage(e);
    }
  },
};
